import type { ImageQualityResult } from '@/@types/latestImagesV2';

/** Shape used to extract tooth numbers from appointment treatments (e.g. TTreatment) */
export interface TreatmentWithTeeth {
  toothNum?: string;
  toothRange?: string;
}

/**
 * Parses a tooth range string (e.g. "2-5") into inclusive tooth numbers.
 * Returns empty array if format is invalid.
 */
function parseToothRange(range: string): string[] {
  const trimmed = range.trim();
  const match = trimmed.match(/^(\d+)\s*-\s*(\d+)$/);
  if (!match) {
    const single = /^\d+$/.test(trimmed) ? [trimmed] : [];
    return single;
  }
  const low = parseInt(match[1], 10);
  const high = parseInt(match[2], 10);
  if (low > high || low < 1 || high > 32) {
    return [];
  }
  const result: string[] = [];
  for (let i = low; i <= high; i++) {
    result.push(String(i));
  }
  return result;
}

/**
 * Returns the set of tooth numbers (1-32, as strings) that are scheduled for treatment.
 * Handles both toothNum (single tooth) and toothRange (e.g. "2-5").
 */
export function getScheduledTeethFromTreatments(
  treatments: TreatmentWithTeeth[],
): Set<string> {
  const set = new Set<string>();
  for (const t of treatments) {
    if (t.toothNum != null && t.toothNum.trim() !== '') {
      const num = t.toothNum.trim();
      if (/^\d+$/.test(num)) {
        const n = parseInt(num, 10);
        if (n >= 1 && n <= 32) set.add(num);
      }
    }
    if (t.toothRange != null && t.toothRange.trim() !== '') {
      for (const tooth of parseToothRange(t.toothRange)) {
        set.add(tooth);
      }
    }
  }
  return set;
}

/**
 * Maps FMX slot indices (0-17) to the teeth numbers expected in that slot.
 * Standard FMX layout for adult dentition.
 */
export const FMX_SLOT_TO_TEETH: Record<number, Set<string>> = {
  0: new Set(['1', '2', '3']),
  1: new Set(['3', '4', '5']),
  2: new Set(['5', '6', '7']),
  3: new Set(['8', '9']),
  4: new Set(['10', '11', '12']),
  5: new Set(['12', '13', '14']),
  6: new Set(['14', '15', '16']),
  7: new Set(['1', '2', '3', '30', '31', '32']),
  8: new Set(['4', '5', '28', '29']),
  9: new Set(['12', '13', '20', '21']),
  10: new Set(['14', '15', '16', '17', '18', '19']),
  11: new Set(['30', '31', '32']),
  12: new Set(['28', '29', '30']),
  13: new Set(['26', '27', '28']),
  14: new Set(['24', '25']),
  15: new Set(['21', '22', '23']),
  16: new Set(['19', '20', '21']),
  17: new Set(['17', '18', '19']),
};

/**
 * Filters teeth to only those in the scheduled set.
 * If scheduledTeeth is null, undefined, or empty, returns [] so no warnings show.
 * Only when scheduledTeeth is a non-empty set do we filter to those teeth.
 */
function filterTeethByScheduled(
  teeth: string[],
  scheduledTeeth: Set<string> | null | undefined,
): string[] {
  if (scheduledTeeth == null || scheduledTeeth.size === 0) {
    return [];
  }
  return teeth.filter((t) => scheduledTeeth.has(t));
}

export type ImageFindingType =
  | 'missing_root_tips'
  | 'missing_image'
  | 'missing_tooth'
  | 'bitewing_overlap'
  | 'cut_off_edges'
  | 'cej_not_visible';

/**
 * Priority order for findings (lower number = higher priority)
 * Used to deterministically sort findings so the most important shows first
 */
const FINDING_PRIORITY: Record<ImageFindingType, number> = {
  missing_tooth: 1,
  missing_root_tips: 2,
  cut_off_edges: 3,
  cej_not_visible: 4,
  bitewing_overlap: 5,
  missing_image: 6,
};

export interface ImageFinding {
  type: ImageFindingType;
  label: string;
  severity: 'warning' | 'error';
  affectedTeeth?: string[];
}

interface ImageWarningState {
  overlap: boolean;
  missingRootTipsTeeth: string[];
  cutOffEdgesTeeth: string[];
  missingTeeth: string[];
  cejIssuesTeeth: string[];
}

function getImageWarningState(
  imageData: ImageQualityResult,
  scheduledTeeth?: Set<string> | null,
): ImageWarningState {
  const slotHasScheduledTooth =
    scheduledTeeth != null &&
    scheduledTeeth.size > 0 &&
    imageData.teeth.some((t) => scheduledTeeth.has(t.tooth_num));

  const missingRootTipsTeeth = filterTeethByScheduled(
    imageData.teeth
      .filter((tooth) => tooth.root_visible === false)
      .map((tooth) => tooth.tooth_num)
      .sort(),
    scheduledTeeth,
  );

  const cutOffEdgesTeeth = filterTeethByScheduled(
    imageData.teeth
      .filter((tooth) => tooth.cut_off_edges && tooth.cut_off_edges.length > 0)
      .map((tooth) => tooth.tooth_num)
      .sort(),
    scheduledTeeth,
  );

  const missingTeeth = filterTeethByScheduled(
    imageData.teeth
      .filter((tooth) => tooth.is_missing)
      .map((tooth) => tooth.tooth_num)
      .sort(),
    scheduledTeeth,
  );

  const cejIssuesTeeth = filterTeethByScheduled(
    imageData.teeth
      .filter(
        (tooth) =>
          tooth.cej_mesial_visible === false ||
          tooth.cej_distal_visible === false,
      )
      .map((tooth) => tooth.tooth_num)
      .sort(),
    scheduledTeeth,
  );

  return {
    overlap: Boolean(imageData.is_flagged && slotHasScheduledTooth),
    missingRootTipsTeeth,
    cutOffEdgesTeeth,
    missingTeeth,
    cejIssuesTeeth,
  };
}

/**
 * Derives image findings from image quality data for a specific image.
 * Image quality is already scoped to the image's FMX slot by the backend.
 * Findings are sorted by priority (most important first).
 *
 * @param imageId - The image ID to get findings for
 * @param imageQuality - Array of image quality results (slot-scoped from backend)
 * @param scheduledTeeth - Optional set of tooth numbers for the current appointment. If provided,
 *                         only findings for those teeth are shown.
 */
export function getImageFindingsFromImageQuality(
  imageId: number,
  imageQuality?: ImageQualityResult[],
  scheduledTeeth?: Set<string> | null,
): ImageFinding[] {
  if (!imageQuality) {
    return [];
  }

  const imageData = imageQuality.find((img) => img.image_id === imageId);
  if (!imageData) {
    return [];
  }

  const findings: ImageFinding[] = [];
  const warningState = getImageWarningState(imageData, scheduledTeeth);

  if (warningState.overlap) {
    findings.push({
      type: 'bitewing_overlap',
      label: 'Overlap detected',
      severity: 'warning',
    });
  }

  if (warningState.missingRootTipsTeeth.length > 0) {
    findings.push({
      type: 'missing_root_tips',
      label: 'Root tips not visible',
      severity: 'warning',
      affectedTeeth: warningState.missingRootTipsTeeth,
    });
  }

  if (warningState.cutOffEdgesTeeth.length > 0) {
    findings.push({
      type: 'cut_off_edges',
      label: 'Edges cut off',
      severity: 'warning',
      affectedTeeth: warningState.cutOffEdgesTeeth,
    });
  }

  if (warningState.missingTeeth.length > 0) {
    findings.push({
      type: 'missing_tooth',
      label: 'Tooth missing',
      severity: 'warning',
      affectedTeeth: warningState.missingTeeth,
    });
  }

  if (warningState.cejIssuesTeeth.length > 0) {
    findings.push({
      type: 'cej_not_visible',
      label: 'CEJ not visible',
      severity: 'warning',
      affectedTeeth: warningState.cejIssuesTeeth,
    });
  }

  return findings.sort(
    (a, b) => FINDING_PRIORITY[a.type] - FINDING_PRIORITY[b.type],
  );
}

/**
 * Creates a map of image ID to findings for efficient lookup
 */
export function createImageFindingsMap(
  imageQuality?: ImageQualityResult[],
): Map<number, ImageFinding[]> {
  const findingsMap = new Map<number, ImageFinding[]>();

  if (!imageQuality) {
    return findingsMap;
  }

  for (const imageData of imageQuality) {
    const findings = getImageFindingsFromImageQuality(
      imageData.image_id,
      imageQuality,
    );
    if (findings.length > 0) {
      findingsMap.set(imageData.image_id, findings);
    }
  }

  return findingsMap;
}

/**
 * Returns slot indices (0-17) that are expected to show the given tooth.
 */
export function getSlotsCoveringTooth(tooth: string): number[] {
  const slots: number[] = [];
  for (let slot = 0; slot <= 17; slot++) {
    if (FMX_SLOT_TO_TEETH[slot]?.has(tooth)) {
      slots.push(slot);
    }
  }
  return slots;
}

/**
 * Returns scheduled teeth that have no image in the FMX viewer (no slot covering that tooth has an image).
 */
export function getScheduledTeethWithMissingImage(
  scheduledTeeth: Set<string>,
  slotsWithImages: Set<number>,
): string[] {
  const result: string[] = [];
  for (const tooth of scheduledTeeth) {
    const slots = getSlotsCoveringTooth(tooth);
    const hasImage = slots.some((s) => slotsWithImages.has(s));
    if (!hasImage) {
      result.push(tooth);
    }
  }
  return result.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
}

/**
 * Returns the smallest set of FMX slot indices (that currently have no image)
 * such that every scheduled tooth with no image is covered by at least one slot.
 * Uses greedy set cover so we show the minimum number of "Missing image" slots.
 */
export function getExpectedSlotsForMissingImage(
  scheduledTeeth: Set<string>,
  slotsWithImages: Set<number>,
): Set<number> {
  const missing = getScheduledTeethWithMissingImage(
    scheduledTeeth,
    slotsWithImages,
  );
  if (missing.length === 0) return new Set<number>();

  const uncovered = new Set(missing);
  const chosen = new Set<number>();

  while (uncovered.size > 0) {
    let bestSlot: number | null = null;
    let bestCount = 0;

    for (let slot = 0; slot <= 17; slot++) {
      if (chosen.has(slot) || slotsWithImages.has(slot)) continue;
      const teethInSlot = FMX_SLOT_TO_TEETH[slot];
      if (!teethInSlot) continue;
      let count = 0;
      for (const t of teethInSlot) {
        if (uncovered.has(t)) count++;
      }
      if (count > bestCount) {
        bestCount = count;
        bestSlot = slot;
      }
    }

    if (bestSlot == null || bestCount === 0) break;

    chosen.add(bestSlot);
    const teethInSlot = FMX_SLOT_TO_TEETH[bestSlot];
    if (teethInSlot) {
      for (const t of teethInSlot) {
        uncovered.delete(t);
      }
    }
  }

  return chosen;
}

/** Finding kind used by the UI to render translated title/description with t`...` */
export type ImageQualityFindingKind =
  | 'overlap_left_bitewings'
  | 'overlap_right_bitewings'
  | 'root_tips_not_visible'
  | 'edges_cut_off'
  | 'additional_imaging_required'
  | 'cej_not_visible'
  | 'image_missing_for_scheduled_teeth';

export interface ImageQualityFinding {
  id: string;
  /** Kind used for translation in the UI */
  findingKind: ImageQualityFindingKind;
  title: string;
  description: string;
  /** Teeth list for messages that include tooth numbers (e.g. "for #14, 15") */
  teethList?: string;
  imageId?: number;
  toothNum?: string;
  severity: 'warning' | 'error';
  completed: boolean;
}

/**
 * Generates image quality findings from image quality data.
 * Image quality is already scoped to each image's FMX slot by the backend.
 * Findings are observational (what was detected), not task-oriented.
 *
 * @param imageQuality - Array of image quality results (slot-scoped from backend)
 * @param imageTypeMap - Map of image IDs to image types
 * @param filterImageId - Optional image ID to filter findings for a specific image
 * @param scheduledTeeth - Optional set of tooth numbers for the current appointment. If provided,
 *                         only findings for those teeth are shown.
 * @param slotsWithImages - Optional set of FMX slot indices that have images in the viewer. When
 *                          provided with scheduledTeeth, adds an "image(s) missing" finding for
 *                          scheduled teeth that have no covering image.
 */
export function getImageQualityFindingsFromImageQuality(
  imageQuality?: ImageQualityResult[],
  imageTypeMap?: Map<number, string>,
  filterImageId?: number,
  scheduledTeeth?: Set<string> | null,
  slotsWithImages?: Set<number> | null,
): ImageQualityFinding[] {
  if (!imageQuality) {
    return [];
  }

  const filteredImageQuality = filterImageId
    ? imageQuality.filter((img) => img.image_id === filterImageId)
    : imageQuality;

  if (filteredImageQuality.length === 0) {
    if (
      scheduledTeeth != null &&
      scheduledTeeth.size > 0 &&
      slotsWithImages != null
    ) {
      const missingImageTeeth = getScheduledTeethWithMissingImage(
        scheduledTeeth,
        slotsWithImages,
      );
      if (missingImageTeeth.length > 0) {
        const teethList = missingImageTeeth.join(', ');
        return [
          {
            id: 'finding-0',
            findingKind: 'image_missing_for_scheduled_teeth',
            title: 'Image(s) missing',
            description: `No image in FMX viewer for teeth #${teethList}.`,
            teethList,
            severity: 'warning',
            completed: false,
          },
        ];
      }
    }
    return [];
  }

  const findings: ImageQualityFinding[] = [];
  let findingId = 0;

  const leftOverlapImages: ImageQualityResult[] = [];
  const rightOverlapImages: ImageQualityResult[] = [];
  const missingRootTipsTeeth = new Set<string>();
  const cutOffTeeth = new Set<string>();
  const missingTeethInImages = new Set<string>();
  const cejNotVisibleTeeth = new Set<string>();

  for (const img of filteredImageQuality) {
    const warningState = getImageWarningState(img, scheduledTeeth);

    if (warningState.overlap) {
      const imageType = imageTypeMap?.get(img.image_id) ?? img.image_type;
      const isLeft = img.teeth.some((tooth) => {
        const toothNum = parseInt(tooth.tooth_num, 10);
        return (
          (toothNum >= 17 && toothNum <= 24) || (toothNum >= 25 && toothNum <= 32)
        );
      });

      if (imageType === 'bitewing' || imageType === 'BITEWING') {
        if (isLeft) {
          leftOverlapImages.push(img);
        } else {
          rightOverlapImages.push(img);
        }
      }
    }

    for (const tooth of warningState.missingRootTipsTeeth) {
      missingRootTipsTeeth.add(tooth);
    }
    for (const tooth of warningState.cutOffEdgesTeeth) {
      cutOffTeeth.add(tooth);
    }
    for (const tooth of warningState.missingTeeth) {
      missingTeethInImages.add(tooth);
    }
    for (const tooth of warningState.cejIssuesTeeth) {
      cejNotVisibleTeeth.add(tooth);
    }
  }

  if (leftOverlapImages.length > 0) {
    findings.push({
      id: `finding-${findingId++}`,
      findingKind: 'overlap_left_bitewings',
      title: 'Interproximal overlap (left bitewings)',
      description: 'Overlap detected on bitewing image(s).',
      severity: 'warning',
      completed: false,
    });
  }

  if (rightOverlapImages.length > 0) {
    findings.push({
      id: `finding-${findingId++}`,
      findingKind: 'overlap_right_bitewings',
      title: 'Interproximal overlap (right bitewings)',
      description: 'Overlap detected on bitewing image(s).',
      severity: 'warning',
      completed: false,
    });
  }

  if (missingRootTipsTeeth.size > 0) {
    const teethList = Array.from(missingRootTipsTeeth).sort().join(', ');
    findings.push({
      id: `finding-${findingId++}`,
      findingKind: 'root_tips_not_visible',
      title: 'Root tips not visible',
      description: `Root tips not visible for #${teethList}.`,
      teethList,
      severity: 'warning',
      completed: false,
    });
  }

  if (cutOffTeeth.size > 0) {
    const teethList = Array.from(cutOffTeeth).sort().join(', ');
    findings.push({
      id: `finding-${findingId++}`,
      findingKind: 'edges_cut_off',
      title: 'Teeth cut off at edges',
      description: `Teeth cut off at edges for #${teethList}.`,
      teethList,
      severity: 'warning',
      completed: false,
    });
  }

  if (missingTeethInImages.size > 0) {
    const teethList = Array.from(missingTeethInImages).sort().join(', ');
    findings.push({
      id: `finding-${findingId++}`,
      findingKind: 'additional_imaging_required',
      title: 'Additional imaging may be required',
      description: `Missing or unclear for #${teethList}; additional imaging may be required.`,
      teethList,
      severity: 'warning',
      completed: false,
    });
  }

  if (cejNotVisibleTeeth.size > 0) {
    const teethList = Array.from(cejNotVisibleTeeth).sort().join(', ');
    findings.push({
      id: `finding-${findingId++}`,
      findingKind: 'cej_not_visible',
      title: 'CEJ not visible',
      description: `CEJ not visible for #${teethList}.`,
      teethList,
      severity: 'warning',
      completed: false,
    });
  }

  // Image missing for scheduled teeth (no image in FMX viewer for those teeth)
  if (
    scheduledTeeth != null &&
    scheduledTeeth.size > 0 &&
    slotsWithImages != null
  ) {
    const missingImageTeeth = getScheduledTeethWithMissingImage(
      scheduledTeeth,
      slotsWithImages,
    );
    if (missingImageTeeth.length > 0) {
      const teethList = missingImageTeeth.join(', ');
      findings.push({
        id: `finding-${findingId++}`,
        findingKind: 'image_missing_for_scheduled_teeth',
        title: 'Image(s) missing',
        description: `No image in FMX viewer for teeth #${teethList}.`,
        teethList,
        severity: 'warning',
        completed: false,
      });
    }
  }

  return findings;
}
